import type { MessageEnrichment } from "@/lib/chat-message-state";

/** Dedup por message.id — usado no merge client-first do chat. */
export type ChatMessageLike = { id?: string; created_at?: string };

export function compareMessagesChronologically<T extends ChatMessageLike>(a: T, b: T): number {
  const aTime = a.created_at ? Date.parse(a.created_at) : 0;
  const bTime = b.created_at ? Date.parse(b.created_at) : 0;
  if (aTime !== bTime) return aTime - bTime;
  const aId = a.id ?? "";
  const bId = b.id ?? "";
  return aId.localeCompare(bId);
}

export function sortMessagesChronologically<T extends ChatMessageLike>(messages: T[]): T[] {
  if (messages.length < 2) return messages;
  const sorted = [...messages].sort(compareMessagesChronologically);
  if (sorted.every((message, index) => message === messages[index])) return messages;
  return sorted;
}

export function sortMessagesByChannel<T extends ChatMessageLike>(
  byChannel: Record<string, T[]>,
): Record<string, T[]> {
  let changed = false;
  const sorted: Record<string, T[]> = {};

  for (const [channel, messages] of Object.entries(byChannel)) {
    const next = sortMessagesChronologically(messages);
    sorted[channel] = next;
    if (
      next.length !== messages.length ||
      next.some((message, index) => message !== messages[index])
    ) {
      changed = true;
    }
  }

  return changed ? sorted : byChannel;
}

type EnrichableMessage = ChatMessageLike & {
  reactions?: Array<{ reaction: string; from_user: string }>;
  sent_by_member_id?: string | null;
  sent_by_member?: { display_name: string | null } | null;
  deleted_by_member_id?: string | null;
  deleted_by_member?: { display_name: string | null } | null;
};

export function dedupeAppendMessages<T extends ChatMessageLike>(
  existing: T[],
  incoming: T[],
): T[] {
  if (incoming.length === 0) return existing;
  const existingIds = new Set(
    existing.map((message) => message.id).filter((id): id is string => Boolean(id)),
  );
  const toAppend = incoming.filter((message) => !message.id || !existingIds.has(message.id));
  if (toAppend.length === 0) return existing;
  return [...existing, ...toAppend];
}

export function mergeMessagesByChannel<T extends ChatMessageLike>(
  prev: Record<string, T[]>,
  incoming: Record<string, T[]>,
): Record<string, T[]> {
  let changed = false;
  const updated = { ...prev };

  for (const [channel, messages] of Object.entries(incoming)) {
    const existing = updated[channel] || [];
    const merged = sortMessagesChronologically(dedupeAppendMessages(existing, messages));
    if (merged.length !== existing.length || merged.some((message, index) => message !== existing[index])) {
      changed = true;
      updated[channel] = merged;
    }
  }

  return changed ? updated : prev;
}

/** Merge seguro: fetch inicial não sobrescreve mensagens já recebidas via realtime. */
export function mergeInitialMessagesLoad<T extends ChatMessageLike>(
  prev: Record<string, T[]>,
  fetched: Record<string, T[]>,
): Record<string, T[]> {
  if (Object.keys(prev).length === 0) return sortMessagesByChannel(fetched);
  return mergeMessagesByChannel(prev, fetched);
}

/** Upsert por id — confirmação de envio e UPDATEs realtime sem perder estado local. */
export function upsertMessagesByChannel<T extends ChatMessageLike>(
  prev: Record<string, T[]>,
  incoming: Record<string, T[]>,
  mergeItem: (existing: T, incoming: T) => T,
): Record<string, T[]> {
  let changed = false;
  const updated = { ...prev };

  for (const [channel, messages] of Object.entries(incoming)) {
    const existing = [...(updated[channel] || [])];
    let channelChanged = false;

    for (const message of messages) {
      if (!message.id) continue;
      const index = existing.findIndex((item) => item.id === message.id);
      if (index === -1) {
        existing.push(message);
        channelChanged = true;
        continue;
      }
      const merged = mergeItem(existing[index], message);
      if (merged !== existing[index]) {
        existing[index] = merged;
        channelChanged = true;
      }
    }

    if (channelChanged) {
      updated[channel] = sortMessagesChronologically(existing);
      changed = true;
    }
  }

  return changed ? updated : prev;
}

/** Aplica enrichment (reações/membros) sem alterar ordem ou ids das mensagens. */
export function mergeEnrichmentIntoChannelMessages<T extends EnrichableMessage>(
  prev: Record<string, T[]>,
  enrichment: MessageEnrichment,
): Record<string, T[]> {
  let changed = false;
  const updated = { ...prev };

  for (const [channel, messages] of Object.entries(prev)) {
    const nextMessages = messages.map((message) => {
      if (!message.id) return message;

      const reactions = enrichment.reactionsByMessageId.get(message.id);
      const sentByMember = message.sent_by_member_id
        ? enrichment.membersById.get(message.sent_by_member_id)
        : undefined;
      const deletedByMember = message.deleted_by_member_id
        ? enrichment.membersById.get(message.deleted_by_member_id)
        : undefined;

      if (!reactions && !sentByMember && !deletedByMember) return message;

      changed = true;
      return {
        ...message,
        ...(reactions ? { reactions } : {}),
        ...(sentByMember ? { sent_by_member: sentByMember } : {}),
        ...(deletedByMember ? { deleted_by_member: deletedByMember } : {}),
      };
    });

    if (nextMessages.some((message, index) => message !== messages[index])) {
      updated[channel] = nextMessages;
    }
  }

  return changed ? updated : prev;
}
